import React, { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ChevronRight } from "lucide-react";
import { HERO_SLIDES } from "../data/catalog";

export const HeroCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 32 });
  const [selectedIdx, setSelectedIdx] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIdx(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();

    const interval = setInterval(() => emblaApi.scrollNext(), 6500);
    return () => {
      clearInterval(interval);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section data-testid="hero-carousel" className="relative bg-leduro-navy overflow-hidden">
      <div className="relative" ref={emblaRef}>
        <div className="flex">
          {HERO_SLIDES.map((slide, i) => (
            <div key={i} className="relative shrink-0 grow-0 basis-full">
              <div className="relative h-[560px] md:h-[640px] lg:h-[720px] w-full">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="absolute inset-0 h-full w-full object-cover opacity-50"
                  loading={i === 0 ? "eager" : "lazy"}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-leduro-navy via-leduro-navy/85 to-transparent" />
                <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />

                <div className="relative h-full leduro-container flex items-center">
                  <div className="max-w-2xl text-white animate-fade-up">
                    <span className="inline-flex items-center gap-2 text-leduro-orange text-[11px] tracking-[0.32em] uppercase font-bold">
                      <span className="h-px w-8 bg-leduro-orange" />
                      {slide.eyebrow}
                    </span>
                    <h1 className="mt-5 text-white font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-balance leading-[1.02]">
                      {slide.title}
                    </h1>
                    <p className="mt-6 text-white/80 text-base md:text-lg max-w-xl leading-relaxed">
                      {slide.subtitle}
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                      <Link to={slide.cta.to} data-testid={`hero-cta-primary-${i}`} className="leduro-btn-primary">
                        {slide.cta.label}
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                      <Link to={slide.cta2.to} data-testid={`hero-cta-secondary-${i}`} className="inline-flex items-center gap-2 text-white text-sm font-semibold uppercase tracking-wider border-b-2 border-white/40 pb-1 hover:border-leduro-orange hover:text-leduro-orange transition-colors">
                        {slide.cta2.label}
                      </Link>
                    </div>
                  </div>

                  {/* Badge */}
                  <div className="hidden md:flex absolute right-12 lg:right-24 top-1/2 -translate-y-1/2 flex-col items-center">
                    <div className="relative w-40 h-40 lg:w-52 lg:h-52 bg-white border-2 border-leduro-navy clip-corner flex items-center justify-center">
                      <div className="text-center px-4">
                        <div className="text-leduro-orange font-heading font-black text-5xl lg:text-6xl leading-none">
                          {String(i + 1).padStart(2, "0")}
                        </div>
                        <div className="mt-3 text-leduro-navy text-[10px] tracking-[0.22em] font-bold uppercase">
                          {slide.badge}
                        </div>
                      </div>
                      {/* corner ticks */}
                      <span className="absolute -top-px -left-px h-3 w-3 border-l-2 border-t-2 border-leduro-orange" />
                      <span className="absolute -top-px -right-px h-3 w-3 border-r-2 border-t-2 border-leduro-orange" />
                      <span className="absolute -bottom-px -left-px h-3 w-3 border-l-2 border-b-2 border-leduro-orange" />
                      <span className="absolute -bottom-px -right-px h-3 w-3 border-r-2 border-b-2 border-leduro-orange" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="leduro-container flex items-end justify-between py-5">
          <div className="flex items-center gap-3">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi?.scrollTo(i)}
                data-testid={`hero-dot-${i}`}
                aria-label={`Go to slide ${i + 1}`}
                className="relative h-1 w-12 bg-white/25 overflow-hidden"
              >
                {selectedIdx === i && (
                  <span className="absolute inset-0 bg-leduro-orange origin-left animate-[fade-up_0.5s_ease-out]" />
                )}
                {selectedIdx === i && (
                  <span className="absolute inset-0 bg-leduro-orange" />
                )}
              </button>
            ))}
            <span className="ml-3 text-white/60 font-mono text-xs tracking-widest">
              {String(selectedIdx + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={scrollPrev}
              data-testid="hero-prev-btn"
              aria-label="Previous slide"
              className="h-11 w-11 grid place-items-center border border-white/30 text-white hover:bg-leduro-orange hover:border-leduro-orange transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              onClick={scrollNext}
              data-testid="hero-next-btn"
              aria-label="Next slide"
              className="h-11 w-11 grid place-items-center border border-white/30 text-white hover:bg-leduro-orange hover:border-leduro-orange transition-colors"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
